import { useCallback, useEffect, useRef } from "react";

/**
 * Run something now, and fold anything asked for straight after into one more
 * run at the end of a short window.
 *
 * For work that several independent callers ask for on the back of one event.
 * The member page is the case it was written for: a write refreshes the page
 * twice over, once from the mutation that made it and once from the
 * `member_changed` frame the server publishes for it, which arrives down the
 * stream a moment later. Both call the same refresh, the second invalidation
 * aborts the requests the first one started — every query there hands React
 * Query's `signal` to the request — and one write costs two rounds of the same
 * calls and two dips into the loading state.
 *
 * Leading edge, so the screen still moves the instant the write lands rather
 * than a fraction of a second later. What arrives inside the window is
 * absorbed, not dropped: it runs once when the window closes, because the
 * second caller may be a real second change — another till, the nightly sweep —
 * and not the echo of the first.
 *
 * `run` is held in a ref, so a caller passing a fresh arrow function every
 * render neither restarts the window nor goes stale.
 */
export function useCoalesced(run: () => void, windowMs: number): () => void {
  const latest = useRef(run);
  useEffect(() => {
    latest.current = run;
  }, [run]);

  const settling = useRef<ReturnType<typeof setTimeout> | null>(null);
  const again = useRef(false);

  useEffect(
    () => () => {
      if (settling.current) clearTimeout(settling.current);
      settling.current = null;
    },
    [],
  );

  return useCallback(() => {
    const hold = () => {
      settling.current = setTimeout(() => {
        settling.current = null;
        if (!again.current) return;
        again.current = false;
        latest.current();
        hold();
      }, windowMs);
    };
    if (settling.current) {
      again.current = true;
      return;
    }
    latest.current();
    hold();
  }, [windowMs]);
}
